import React, { useEffect, useMemo, useState, Component } from 'react';
import { X, Check, Link as LinkIcon, ArrowRight, WhatsappLogo, EnvelopeSimple, ClipboardText } from '@phosphor-icons/react';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { ReviewRequest } from '../dashboard/PendingReviewRequest';
import { PAGE_ROUTE } from '../../lib/routes';
import { cn, getSession, getRentedProperties, getArchiveListForSupply } from '../../lib/utils';
interface RequestReviewModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (request: ReviewRequest) => void;
  navigate?: (page: string, context?: any) => void;
  mode?: 'demand' | 'supply';
  preselectedTenantName?: string;
  preselectedTenantSlug?: string;
  preselectedPropertyTitle?: string;
}
type Phase = 'form' | 'share';
function composeMessageDemand(opts: {
  otherName: string;
  periodStart: string;
  periodEnd: string;
  reference: string;
  link: string;
}) {
  const {
    otherName,
    periodStart,
    periodEnd,
    reference,
    link
  } = opts;
  const refPart = reference.trim() ? ` din ${reference.trim()}` : '';
  const periodPart = periodStart && periodEnd ? ` între ${periodStart.trim()} și ${periodEnd.trim()}` : periodStart ? ` din ${periodStart.trim()}` : '';
  return `Salut ${otherName.trim()}, am stat în chirie la tine${refPart}${periodPart}. Acum caut iar o locuință și un review de la tine m-ar ajuta enorm la următorul proprietar. Durează doar 2 minute:\n\n${link}\n\nMulțumesc!`;
}
function composeMessageSupply(opts: {
  otherName: string;
  periodStart: string;
  periodEnd: string;
  reference: string;
  link: string;
}) {
  const {
    otherName,
    periodStart,
    periodEnd,
    reference,
    link
  } = opts;
  const refPart = reference.trim() ? ` în ${reference.trim()}` : '';
  const periodPart = periodStart && periodEnd ? ` între ${periodStart.trim()} și ${periodEnd.trim()}` : periodStart ? ` din ${periodStart.trim()}` : '';
  return `Salut ${otherName.trim()}, ai stat în chirie la mine${refPart}${periodPart}. Mi-ar prinde tare bine o recenzie pe profilul meu de Izio — îi ajută pe alți chiriași să aibă încredere când le trimit oferte. Durează 2 minute:\n\n${link}\n\nMulțumesc!`;
}
function composeMessageAgent(opts: {
  otherName: string;
  reference: string;
  link: string;
  agencyName?: string;
}) {
  const {
    otherName,
    reference,
    link,
    agencyName
  } = opts;
  const refPart = reference.trim() ? ` ${reference.trim()}` : '';
  const agencyPart = agencyName ? ` și ${agencyName}` : '';
  return `Salut ${otherName.trim()}! Mă bucur că am reușit să te ajut să găsești${refPart}. Dacă ai un minut, m-ar ajuta enorm o recenzie despre experiența cu mine${agencyPart} pe Izio: ${link} Mulțumesc!`;
}
export function RequestReviewModal({
  open,
  onClose,
  onSuccess,
  navigate,
  mode = 'demand',
  preselectedTenantName,
  preselectedTenantSlug,
  preselectedPropertyTitle
}: RequestReviewModalProps) {
  const isSupply = mode === 'supply';
  const session = getSession();
  const isAgent = isSupply && session?.subRole === 'agent';
  const [phase, setPhase] = useState<Phase>('form');
  const [landlordName, setLandlordName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [reference, setReference] = useState('');
  const [selectedRentalId, setSelectedRentalId] = useState<string>('');
  const [token, setToken] = useState('');
  const [message, setMessage] = useState('');
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  // Fetch rental history for agents
  const rentalHistory = useMemo(() => {
    if (!isAgent || !session?.email) return [];
    // Get rented properties
    const rented = getRentedProperties(session.email).map(p => ({
      id: p.id,
      tenantName: p.rentedTenantName || 'Chiriaș',
      tenantSlug: (p as any).rentedTenantSlug || '',
      propertyTitle: p.title || (p as any).name || 'Proprietate',
      date: p.rentedAt || p.createdAt
    }));
    // Get closures from archive
    const archive = getArchiveListForSupply(session.email).filter(a => a.kind === 'closure').map(a => {
      if (a.kind !== 'closure') return null;
      return {
        id: a.property.id,
        tenantName: a.succesOffer?.toName || a.property.rentedTenantName || 'Chiriaș',
        tenantSlug: a.succesOffer?.toSlug || (a.property as any).rentedTenantSlug || '',
        propertyTitle: a.property.title || (a.property as any).name || 'Proprietate',
        date: a.closedAt
      };
    }).filter(Boolean) as typeof rented;
    // Merge and deduplicate by property ID
    const merged = [...rented, ...archive];
    const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
    // Sort by date desc
    return unique.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [isAgent, session?.email]);
  // Reset on open
  useEffect(() => {
    if (open) {
      if (preselectedTenantName && preselectedPropertyTitle) {
        // Contextual mode (opened from callout)
        setLandlordName(preselectedTenantName);
        setReference(preselectedPropertyTitle);
        generateMessage(preselectedTenantName, preselectedPropertyTitle);
      } else {
        // Manual mode
        setPhase('form');
        setLandlordName(preselectedTenantName || '');
        setPeriodStart('');
        setPeriodEnd('');
        setReference('');
        setSelectedRentalId('');
        setToken('');
        setMessage('');
      }
      setCopiedMessage(false);
      setCopiedLink(false);
    }
  }, [open, preselectedTenantName, preselectedPropertyTitle]);
  // Handle rental selection from dropdown
  const handleRentalSelect = (id: string) => {
    setSelectedRentalId(id);
    if (id === 'manual') {
      setLandlordName('');
      setReference('');
      return;
    }
    const rental = rentalHistory.find(r => r.id === id);
    if (rental) {
      setLandlordName(rental.tenantName);
      setReference(rental.propertyTitle);
    }
  };
  // Escape
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);
  // Lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  const isValid = isAgent ? landlordName.trim().length > 0 && reference.trim().length > 0 : landlordName.trim().length > 0 && periodStart.trim().length > 0 && periodEnd.trim().length > 0;
  const reviewUrl = useMemo(() => token && typeof window !== 'undefined' ? `${window.location.origin}/proprietar/${session?.slug || 'agent'}?openReview=true` : '', [token, session?.slug]);
  if (!open) return null;
  const generateMessage = (name: string, ref: string, start?: string, end?: string) => {
    const newToken = Math.random().toString(36).slice(2, 10);
    const newUrl = typeof window !== 'undefined' ? `${window.location.origin}/proprietar/${session?.slug || 'agent'}?openReview=true` : `/proprietar/agent?openReview=true`;
    let autoMessage = '';
    if (isAgent) {
      autoMessage = composeMessageAgent({
        otherName: name,
        reference: ref,
        link: newUrl,
        agencyName: session?.agencyName
      });
    } else if (isSupply) {
      autoMessage = composeMessageSupply({
        otherName: name,
        periodStart: start || '',
        periodEnd: end || '',
        reference: ref,
        link: newUrl
      });
    } else {
      autoMessage = composeMessageDemand({
        otherName: name,
        periodStart: start || '',
        periodEnd: end || '',
        reference: ref,
        link: newUrl
      });
    }
    setToken(newToken);
    setMessage(autoMessage);
    setPhase('share');
    const newRequest: ReviewRequest = {
      id: Math.random().toString(36).slice(2, 10),
      landlordName: name.trim(),
      landlordEmail: '',
      token: newToken,
      sentAt: 'Azi',
      status: 'sent'
    };
    onSuccess(newRequest);
  };
  const handleGenerate = () => {
    if (!isValid) return;
    generateMessage(landlordName, reference, periodStart, periodEnd);
  };
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedMessage(true);
      setTimeout(() => setCopiedMessage(false), 2000);
    } catch {
      // ignore
    }
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(reviewUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // ignore
    }
  };
  const handleWhatsApp = () => {
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encoded}`, '_blank', 'noopener,noreferrer');
  };
  const handleEmail = () => {
    const subject = encodeURIComponent('Recenzie Izio');
    const body = encodeURIComponent(message);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };
  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await (navigator as any).share({
          title: 'Cerere recenzie Izio',
          text: message
        });
        return;
      } catch {
        // user cancelled, fall through
      }
    }
    handleCopyMessage();
  };
  const openLink = () => {
    if (!token) return;
    // In demand mode the requester is the tenant — landlord/agent is asked to
    // review them. In supply mode, the requester is the landlord/agent — the
    // tenant is asked to review them. Categories + copy on the review page
    // depend on this subject type.
    // subjectType = who is being reviewed by the recipient of the link.
    //  • demand mode: requester is the tenant (Olivia) — landlord/agent will
    //    review the tenant. subject = tenant.
    //  • supply mode: requester is the landlord/agent — tenant will review
    //    the landlord/agent. subject = landlord or agent.
    const subjectType: 'tenant' | 'landlord' | 'agent' = isSupply ? isAgent ? 'agent' : 'landlord' : 'tenant';
    // subjectName = name of the person being reviewed (= the requester, since
    // they're asking someone else to review THEM). The landlordName field is
    // just metadata for the WhatsApp greeting, not the review subject.
    const subjectName = session?.name || (isSupply ? 'Agentul' : 'Chiriașul');
    const subjectSlug = session?.slug || '';
    // targetSlug = slug of the intended recipient (the person who SHOULD be
    // writing the review). Used for strict access check on the review page.
    const selectedRental = rentalHistory.find(r => r.id === selectedRentalId);
    const targetSlug = isSupply ? preselectedTenantSlug || selectedRental?.tenantSlug || '' : '';
    if (navigate) {
      navigate(PAGE_ROUTE.REVIEW_SUBMISSION, {
        token,
        subjectType,
        subjectName,
        subjectSlug,
        targetSlug
      });
      onClose();
    } else {
      window.open(reviewUrl, '_blank', 'noopener,noreferrer');
    }
  };
  const supportsNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;
  return <div role="dialog" aria-modal="true" aria-label={isSupply ? 'Cere o recenzie de la un fost chiriaș' : 'Cere o recenzie'} className="fixed inset-0 z-50 bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={onClose} data-id="element-1826">
      <div className="w-full sm:max-w-lg bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1827">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-1828">
          <h2 className="text-base font-semibold text-light-1" data-id="element-1829">
            {phase === 'form' ? isSupply ? 'Cere o recenzie de la un fost chiriaș' : 'Cere o recenzie' : 'Trimite invitația'}
          </h2>
          <button onClick={onClose} className="text-light-3 hover:text-light-1 transition-colors p-1 -m-1 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-lime-1 cursor-pointer" aria-label="Închide" data-id="element-1830">
            <X size={20} weight="bold" data-id="element-1831" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6" data-id="element-1832">
          {phase === 'form' ? <div className="p-5 sm:p-6 space-y-5" data-id="element-1833">
              <div className="space-y-1.5" data-id="element-1834">
                <p className="text-sm text-light-2 leading-relaxed" data-id="element-1835">
                  {isAgent ? 'Alege o închiriere din istoric sau completează manual detaliile pentru a genera un mesaj de cerere recenzie.' : isSupply ? 'Spune-ne câteva detalii și îți pregătim un mesaj gata de trimis pe WhatsApp către fostul tău chiriaș.' : 'Spune-ne câteva detalii și îți pregătim un mesaj gata de trimis pe WhatsApp către fostul tău proprietar.'}
                </p>
              </div>

              {isAgent && rentalHistory.length > 0 && <div className="space-y-1.5" data-id="element-1836">
                  <label className="block text-xs font-medium text-light-2" data-id="element-1837">
                    Alege închirierea
                  </label>
                  <Select value={selectedRentalId} onChange={e => handleRentalSelect(e.target.value)} options={[{
              value: '',
              label: 'Selectează o închiriere...'
            }, ...rentalHistory.map(r => ({
              value: r.id,
              label: `${r.tenantName} — ${r.propertyTitle}`
            })), {
              value: 'manual',
              label: 'Altă închiriere (introducere manuală)'
            }]} data-id="element-1838" />
                </div>}

              {(!isAgent || selectedRentalId === 'manual' || rentalHistory.length === 0) && <>
                  <div className="space-y-1.5" data-id="element-1839">
                    <label htmlFor="landlordName" className="block text-xs font-medium text-light-2" data-id="element-1840">
                      {isSupply ? 'Numele chiriașului' : 'Numele proprietarului'}
                    </label>
                    <Input id="landlordName" value={landlordName} onChange={e => setLandlordName(e.target.value)} placeholder={isSupply ? 'ex: Andrei' : 'ex: Domnul Radu'} data-id="element-1841" />
                  </div>

                  {!isAgent && <div className="grid grid-cols-2 gap-4" data-id="element-1842">
                      <div className="space-y-1.5" data-id="element-1843">
                        <label htmlFor="periodStart" className="block text-xs font-medium text-light-2" data-id="element-1844">
                          De când a stat
                        </label>
                        <Input id="periodStart" value={periodStart} onChange={e => setPeriodStart(e.target.value)} placeholder="ex: Mai 2024" data-id="element-1845" />
                      </div>
                      <div className="space-y-1.5" data-id="element-1846">
                        <label htmlFor="periodEnd" className="block text-xs font-medium text-light-2" data-id="element-1847">
                          Până când
                        </label>
                        <Input id="periodEnd" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} placeholder="ex: Aprilie 2026" data-id="element-1848" />
                      </div>
                    </div>}

                  <div className="space-y-1.5" data-id="element-1849">
                    <label htmlFor="reference" className="block text-xs font-medium text-light-2" data-id="element-1850">
                      Cum vrei să te referi la locuință{' '}
                      <span className="text-light-4 font-normal" data-id="element-1851">
                        (opțional)
                      </span>
                    </label>
                    <Input id="reference" value={reference} onChange={e => setReference(e.target.value)} placeholder={isSupply ? 'ex: garsoniera mea din Floreasca / apartamentul din Aviatorilor' : 'ex: garsoniera din Floreasca / apartamentul din Aviatorilor'} data-id="element-1852" />
                    <p className="text-[11px] text-light-4" data-id="element-1853">
                      Apare în mesajul automat. Scrie cum ai vorbi natural.
                    </p>
                  </div>
                </>}
            </div> : <div className="space-y-5" data-id="element-1854">
              {/* Editable message */}
              <div className="space-y-1.5" data-id="element-1855">
                <label className="text-xs font-medium text-light-2" data-id="element-1856">
                  Mesajul pe care îl trimiți
                </label>
                <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={7} className="font-normal" data-id="element-1857" />
                <p className="text-xs text-light-4" data-id="element-1858">
                  Editează liber înainte de a trimite.
                </p>
              </div>

              {/* Link preview, clickable for testing */}
              <div className="space-y-1.5" data-id="element-1859">
                <label className="text-xs font-medium text-light-2" data-id="element-1860">
                  Linkul către formularul de recenzie
                </label>
                <div className="flex items-center gap-2 p-2 bg-dark-1 border border-dark-3 rounded-lg" data-id="element-1861">
                  <button type="button" onClick={openLink} className="flex-1 min-w-0 px-2 py-1 text-left text-sm text-lime-1 hover:text-lime-2 truncate transition-colors cursor-pointer" title="Deschide formularul de recenzie (demo)" data-id="element-1862">
                    {reviewUrl}
                  </button>
                  <button type="button" onClick={handleCopyLink} aria-label="Copiază linkul" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium rounded-md transition-colors cursor-pointer" data-id="element-1863">
                    {copiedLink ? <>
                        <Check size={14} weight="bold" className="text-lime-1" data-id="element-1864" />
                        Copiat
                      </> : <>
                        <LinkIcon size={14} weight="regular" data-id="element-1865" />
                        Copiază
                      </>}
                  </button>
                </div>
                <p className="text-xs text-light-4" data-id="element-1866">
                  {isSupply ? 'Apasă pe link pentru a deschide formularul (mod demo — testează ce vede chiriașul).' : 'Apasă pe link pentru a deschide formularul (mod demo — testează ce vede proprietarul).'}
                </p>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-1867">
          {phase === 'form' ? <button type="button" onClick={handleGenerate} disabled={!isValid} className={cn('w-full h-12 rounded-md text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2', isValid ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-1868">
              Pregătește mesajul
              <ArrowRight size={16} weight="bold" data-id="element-1869" />
            </button> : <div className="space-y-3" data-id="element-1870">
              {/* Primary channel — WhatsApp (most popular in RO) */}
              <button type="button" onClick={handleWhatsApp} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-1871">
                <WhatsappLogo size={18} weight="regular" data-id="element-1872" />
                Trimite pe WhatsApp
              </button>

              {/* Secondary channels — visible quick actions */}
              <div className="grid grid-cols-2 gap-2" data-id="element-1873">
                <button type="button" onClick={handleCopyMessage} className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium transition-colors cursor-pointer" data-id="element-1874">
                  {copiedMessage ? <>
                      <Check size={14} weight="bold" className="text-lime-1" data-id="element-1875" />
                      Copiat
                    </> : <>
                      <ClipboardText size={14} weight="regular" data-id="element-1876" />
                      Copiază mesajul
                    </>}
                </button>
                <button type="button" onClick={handleEmail} className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium transition-colors cursor-pointer" data-id="element-1877">
                  <EnvelopeSimple size={14} weight="regular" data-id="element-1878" />
                  Email
                </button>
              </div>

              {/* Footer micro-actions */}
              <div className="flex items-center justify-between pt-1" data-id="element-1879">
                <button type="button" onClick={() => setPhase('form')} className="text-xs text-light-3 hover:text-light-1 hover:underline underline-offset-4 cursor-pointer transition-colors" data-id="element-1880">
                  ← Înapoi
                </button>
                {supportsNativeShare && <button type="button" onClick={handleNativeShare} className="text-xs text-light-3 hover:text-light-1 hover:underline underline-offset-4 cursor-pointer transition-colors" data-id="element-1881">
                    Alte opțiuni
                  </button>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}